import { DataItem } from '../DataItem/index.js'
import styles from './DataGrid.module.scss'

/**
 * @typedef {object} DataGridItem
 * @property {string}            label  - Field label
 * @property {React.ReactNode}   value  - Field value (string, number, or JSX)
 * @property {1|2|3|'full'}     [span=1] - Column span. 'full' = 1/-1 (entire row).
 * @property {boolean}          [full]   - Alias for span='full' (backward compat)
 */

/**
 * Maps `size` prop to the shared CSS vars consumed by DataItem, PriceBlock, and StatusBadge.
 * Setting these on the DataGrid root lets all descendant components inherit the same scale.
 */
const SIZE_VARS = {
  tiny:  { '--bim-data-label-size': '0.75em',  '--bim-data-value-size': '0.815em'   },
  small:  { '--bim-data-label-size': '0.75em',  '--bim-data-value-size': '1em'   },
  normal: { '--bim-data-label-size': '0.875em', '--bim-data-value-size': '1.2em' },
  large:  { '--bim-data-label-size': '1em',     '--bim-data-value-size': '1.4em' },
}

/**
 * DataGrid — arranges DataItem atoms in an N-column grid.
 *
 * Domains build their own items array and pass it here;
 * the result is passed to ProductTile.Content (or any container).
 *
 * **Column mode:**
 * - `columns` provided → `repeat(N, 1fr)` — fixed, forced layout
 * - `columns` omitted  → `repeat(auto-fill, minmax(itemMin, 1fr))` — natural wrap
 *
 * **Span per item:** `span: 1 | 2 | 3 | 'full'`.
 * `full: true` is accepted as an alias for `span: 'full'`.
 *
 * **Footer slot:** optional `footer` renders after the grid, full-width,
 * outside the column constraints. Use for price blocks, CTAs, etc.
 *
 * @param {object}           props
 * @param {DataGridItem[]}   props.items              - Data items to render
 * @param {number}          [props.columns]               - Fixed column count; omit for auto-fill
 * @param {string}          [props.itemMin='5em']         - Min item width in auto-fill mode
 * @param {React.ReactNode} [props.footer]                - Full-width slot rendered below the grid
 * @param {'small'|'normal'|'large'} [props.size='normal'] - Typography scale injected as CSS vars;
 *   inherited by all DataItem, PriceBlock, and StatusBadge descendants.
 * @param {string}          [props.className]             - Extra class on root wrapper
 * @param {string}          [props.gridClassName]         - Extra class on the inner grid div only
 *   (use for padding, borders etc. that should NOT apply to the footer slot)
 */
export function DataGrid({ items = [], columns, itemMin = '5em', footer, size = 'normal', className, gridClassName }) {
  if (!items.length && !footer) return null

  const isFixed    = columns != null
  const rootStyle  = SIZE_VARS[size] ?? SIZE_VARS.normal
  const gridStyle  = isFixed
    ? { '--dg-columns': columns }
    : { '--dg-item-min': itemMin }

  const rootClass = [styles.root, className].filter(Boolean).join(' ')
  const gridClass = [isFixed ? styles.gridFixed : styles.gridAuto, gridClassName].filter(Boolean).join(' ')

  return (
    <div className={rootClass} style={rootStyle}>
      {items.length > 0 && (
        <div className={gridClass} style={gridStyle}>
          {items.map((item, i) => {
            const rawSpan   = item.full ? 'full' : (item.span ?? 1)
            // In fixed-column mode, clamp numeric spans that meet or exceed the column
            // count to 'full' (grid-column: 1 / -1) so they don't create implicit tracks.
            const span      = (isFixed && typeof rawSpan === 'number' && rawSpan >= columns)
                                ? 'full'
                                : rawSpan
            const spanClass = span === 'full' ? styles.spanFull
                            : span === 3     ? styles.span3
                            : span === 2     ? styles.span2
                            : styles.span1

            return (
              <div key={i} className={[styles.cell, spanClass].filter(Boolean).join(' ')}>
                <DataItem label={item.label} value={item.value} tooltip={item.tooltip} />
              </div>
            )
          })}
        </div>
      )}
      {footer && (
        <div className={styles.footer}>{footer}</div>
      )}
    </div>
  )
}
